import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

export interface User {
  id: string;
  name: string;
  email: string;
  householdId?: string;
}

export interface Settings {
  checkedItemsBehavior: 'hide' | 'strikethrough';
  defaultGroceryListId: string;
}

export type FlashMessageType = 'passwordUpdated' | null;

interface AppContextType {
  user: User;
  settings: Settings;
  updateSettings: (settings: Partial<Settings>) => void;
  flashMessage: FlashMessageType;
  setFlashMessage: (msg: FlashMessageType) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Mock data
const mockUser: User = {
  id: 'user1',
  name: 'Alex Thompson',
  email: 'alex@example.com',
  householdId: 'household1',
};

const mockSettings: Settings = {
  checkedItemsBehavior: 'strikethrough',
  defaultGroceryListId: 'list1',
};

const STORAGE_KEY = 'sage_chef_data';

export function AppProvider({ children }: { children: ReactNode }) {
  const [user] = useState<User>(mockUser);
  const [settings, setSettings] = useState<Settings>(mockSettings);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [flashMessage, setFlashMessage] = useState<FlashMessageType>(null);

  // Load settings from AsyncStorage on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const saved = await AsyncStorage.getItem(STORAGE_KEY);
        if (saved) {
          const data = JSON.parse(saved);
          const raw = data.settings ?? {};
          setSettings({
            checkedItemsBehavior: raw.checkedItemsBehavior ?? mockSettings.checkedItemsBehavior,
            defaultGroceryListId: raw.defaultGroceryListId ?? mockSettings.defaultGroceryListId,
          });
        }
      } catch (error) {
        console.error('Failed to load data from AsyncStorage:', error);
      } finally {
        setHasLoaded(true);
      }
    };
    loadData();
  }, []);

  // Save settings to AsyncStorage whenever they change (only after initial load)
  useEffect(() => {
    if (!hasLoaded) return;
    const saveData = async () => {
      try {
        await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify({ settings }));
      } catch (error) {
        console.error('Failed to save data to AsyncStorage:', error);
      }
    };
    saveData();
  }, [hasLoaded, settings]);

  const updateSettings = (updates: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  return (
    <AppContext.Provider
      value={{
        user,
        settings,
        updateSettings,
        flashMessage,
        setFlashMessage,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
